/**
 * JobSummaryEditor — builds the customer-facing summary for a repair quote.
 *
 * Replaces the plain "Consolidated description" box on repair quotes with a
 * structured composer. The customer-facing text is assembled from up to three
 * toggleable sources — the latest Diagnostics job-log, the original check-in
 * Job Summary, and the Device Details — plus a free-text Additional notes box.
 *
 * Nothing here is persisted on its own: the checkbox/notes state is local and
 * resets every time the quote is opened (recompute-on-open). The single
 * persisted artefact is the composed string, pushed up via `onComposedChange`
 * into the quote's existing `consolidatedDescription` field — which is what
 * prints on the quote and invoice and what Generate-Invoice promotes from
 * (it leads the first service's invoice line as "Diagnostics: …").
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Checkbox,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { CheckInDraftJob } from '@techgitter/contracts';

import { useJobLogs } from '../../hooks/useJobLogs';
import { useQuoteSourceDevice } from '../../hooks/useQuoteSourceDevice';
import { checkInDeviceLabel } from '@techgitter/contracts';

export interface JobSummaryEditorProps {
  sm4rtJobId: string | null;
  sourceCheckInDraftJobId: string | null;
  disabled?: boolean;
  /** Receives the composed customer-facing text. Wired to setConsolidatedDescription. */
  onComposedChange: (text: string) => void;
}


/** Latest diagnostics job-log text, mirroring the backend seed
 *  (type === 'diagnostics', newest by createdAt, excluding deleted). */
function latestDiagnosticsText(
  logs: { type: string; text: string; createdAt: string; status: string }[],
): string {
  const diags = logs
    .filter((l) => l.type === 'diagnostics' && l.status !== 'deleted')
    .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
  return diags[0]?.text?.trim() ?? '';
}

/** Customer-facing device line.
 *  - Laptop / All-in-One → manufacturer + serial.
 *  - Branded desktop (manufacturer set and ≠ 'Custom') → manufacturer + serial.
 *  - Custom / unbranded desktop → fixed "Custom Desktop Computer".  */
function formatDeviceDetails(device: CheckInDraftJob | null): string {
  if (!device) return '';
  const insp = device.inspection;
  const manufacturer = insp?.manufacturer?.trim() || '';
  const serial = insp?.serialNumber?.trim() || '';

  if (device.deviceType === 'desktop' && (!manufacturer || manufacturer === 'Custom')) {
    return 'Custom Desktop Computer';
  }

  const label = checkInDeviceLabel(device);
  const head = manufacturer ? `${manufacturer} ${label}` : label;
  return serial ? `${head}, Serial ${serial}` : head;
}

const SectionToggle: React.FC<{
  label: string;
  checked: boolean;
  value: string;
  disabled: boolean;
  onChange: (next: boolean) => void;
}> = ({ label, checked, value, disabled, onChange }) => {
  const hasContent = value.trim().length > 0;
  return (
    <FormControlLabel
      control={
        <Checkbox
          size="small"
          checked={checked && hasContent}
          disabled={disabled || !hasContent}
          onChange={(e) => onChange(e.target.checked)}
        />
      }
      label={
        <Typography variant="body2" component="span">
          {label}
          {!hasContent && (
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              sx={{ ml: 0.5 }}
            >
              (none recorded)
            </Typography>
          )}
        </Typography>
      }
    />
  );
};

export const JobSummaryEditor: React.FC<JobSummaryEditorProps> = ({
  sm4rtJobId,
  sourceCheckInDraftJobId,
  disabled = false,
  onComposedChange,
}) => {
  const { logs } = useJobLogs(sm4rtJobId ?? undefined);
  const { device } = useQuoteSourceDevice({
    sm4rtJobId,
    sourceCheckInDraftJobId,
  });

  const diagnostics = useMemo(() => latestDiagnosticsText(logs), [logs]);
  const jobSummary = useMemo(() => device?.serviceSummary?.trim() ?? '', [device]);
  const deviceDetails = useMemo(() => formatDeviceDetails(device), [device]);

  const [includeDiagnostics, setIncludeDiagnostics] = useState(true);
  const [includeJobSummary, setIncludeJobSummary] = useState(true);
  const [includeDeviceDetails, setIncludeDeviceDetails] = useState(true);
  const [notes, setNotes] = useState('');

  const preview = useMemo(() => {
    return [
      includeDiagnostics ? diagnostics : '',
      includeJobSummary ? jobSummary : '',
      includeDeviceDetails ? deviceDetails : '',
    ]
      .map((s) => s.trim())
      .filter(Boolean)
      .join('\n\n');
  }, [
    includeDiagnostics,
    includeJobSummary,
    includeDeviceDetails,
    diagnostics,
    jobSummary,
    deviceDetails,
  ]);

  const composed = useMemo(
    () => [preview, notes.trim()].filter(Boolean).join('\n\n'),
    [preview, notes],
  );

  // Push the composed customer-facing text up into the quote's
  // consolidatedDescription. The setter is stable and React bails on an
  // identical string, so this won't loop.
  useEffect(() => {
    onComposedChange(composed);
  }, [composed, onComposedChange]);

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Stack spacing={1.5}>
        <Typography variant="subtitle2">Job Summary</Typography>
        <Typography variant="caption" color="text.secondary">
          Shown on the quote and invoice. Untick a section to leave it out.
        </Typography>

        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <SectionToggle
            label="Diagnostics"
            checked={includeDiagnostics}
            value={diagnostics}
            disabled={disabled}
            onChange={setIncludeDiagnostics}
          />
          <SectionToggle
            label="Job Summary"
            checked={includeJobSummary}
            value={jobSummary}
            disabled={disabled}
            onChange={setIncludeJobSummary}
          />
          <SectionToggle
            label="Device Details"
            checked={includeDeviceDetails}
            value={deviceDetails}
            disabled={disabled}
            onChange={setIncludeDeviceDetails}
          />
        </Stack>

        <TextField
          size="small"
          label="Customer-facing summary (preview & invoice)"
          multiline
          minRows={3}
          value={preview}
          InputProps={{ readOnly: true }}
          placeholder="No sections selected"
          fullWidth
        />

        <TextField
          size="small"
          label="Additional notes"
          multiline
          minRows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={disabled}
          placeholder="Optional — appended to the summary above."
          fullWidth
        />
      </Stack>
    </Paper>
  );
};
